import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileFrame } from "@/components/MobileFrame";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import type { Database } from "@/integrations/supabase/types";
import { scanStore, type AIScan } from "@/lib/scanStore";
import { registerChannel, unregisterChannel } from "@/lib/realtimeChannels";

type Job = Database["public"]["Tables"]["bulk_verification_jobs"]["Row"];
type Item = Database["public"]["Tables"]["bulk_verification_items"]["Row"];

// Use theme tokens so bulk review tracks the active palette.
const GOLD = "hsl(var(--marigold))";
const PLUM = "hsl(var(--ink))";
const CREAM = "hsl(var(--cream))";
// Auto-derived contrast-safe foregrounds. These are guaranteed to hit AA
// against their surface in every theme — critical for dark themes like
// "ni-underground" where both --ink and --marigold are light pearls and
// would otherwise vanish into each other on filled pills.
const GOLD_FG = "hsl(var(--marigold-fg))"; // text on GOLD surfaces
const PLUM_FG = "hsl(var(--ink-fg))";       // text on PLUM (ink) surfaces

interface JobWithItems extends Job {
  items: Item[];
}

/**
 * Bulk Review — list of recent bulk jobs with realtime progress and per-item
 * approve/reject controls. Approve prompts "Keep private / Post to feed".
 * Reject just marks the item rejected (no closet entry, no scan refund).
 */
export default function BulkReview() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [jobs, setJobs] = useState<JobWithItems[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  // Combined-post composer: which job is being combined, which item ids are
  // picked, and the shared caption. Only approved items show up as options.
  const [combineJobId, setCombineJobId] = useState<string | null>(null);
  const [combinePicked, setCombinePicked] = useState<Set<string>>(new Set());
  const [combineCaption, setCombineCaption] = useState("");
  const [combinePosting, setCombinePosting] = useState(false);
  // Per-job multi-select state. Only activates once all items in the job
  // have finished processing (no queued/processing rows). Holds the set of
  // currently-picked item ids for the active job.
  const [selectJobId, setSelectJobId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const load = async () => {
      const { data: js } = await supabase
        .from("bulk_verification_jobs")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20);
      const ids = (js ?? []).map((j) => j.id);
      const { data: its } = ids.length
        ? await supabase
            .from("bulk_verification_items")
            .select("*")
            .in("job_id", ids)
            .order("position", { ascending: true })
        : { data: [] as Item[] };
      if (cancelled) return;
      const grouped: JobWithItems[] = (js ?? []).map((j) => ({
        ...j,
        items: (its ?? []).filter((i) => i.job_id === j.id),
      }));
      setJobs(grouped);
      // Auto-open the newest job that still has work to do.
      if (!openId && grouped.length) {
        const target = grouped.find(
          (j) => j.status === "processing" ||
                 j.items.some((i) => i.status === "complete" && i.user_decision === "pending"),
        ) ?? grouped[0];
        setOpenId(target.id);
      }
      setLoading(false);
    };
    void load();
    const key = `bulk-review-${user.id}`;
    registerChannel(key, () =>
      supabase
        .channel(key)
        .on("postgres_changes", { event: "*", schema: "public", table: "bulk_verification_items", filter: `user_id=eq.${user.id}` }, () => void load())
        .on("postgres_changes", { event: "*", schema: "public", table: "bulk_verification_jobs", filter: `user_id=eq.${user.id}` }, () => void load())
        .subscribe()
    );
    return () => { cancelled = true; unregisterChannel(key); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const reject = async (item: Item) => {
    setBusy(item.id);
    const { error } = await supabase
      .from("bulk_verification_items")
      .update({ user_decision: "rejected" })
      .eq("id", item.id);
    setBusy(null);
    if (error) toast.error("Could not reject"); else toast("Item dismissed");
  };

  /** True when every item in the job has finished processing — that's when
   *  multi-select mode becomes available. */
  const isJobFullyVerified = (j: JobWithItems) =>
    j.items.length > 0 &&
    j.items.every((i) => i.status === "complete" || i.status === "failed");

  const toggleSelectMode = (jobId: string) => {
    if (selectJobId === jobId) {
      setSelectJobId(null);
      setSelectedIds(new Set());
    } else {
      setSelectJobId(jobId);
      setSelectedIds(new Set());
    }
  };

  const toggleSelected = (id: string) =>
    setSelectedIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });

  const bulkApply = async (
    job: JobWithItems,
    kind: "closet" | "post" | "reject",
  ) => {
    const picked = job.items.filter(
      (i) => selectedIds.has(i.id) && i.status === "complete" && i.user_decision === "pending",
    );
    if (picked.length === 0) {
      toast.error("Pick at least one pending item");
      return;
    }
    setBulkBusy(true);
    try {
      for (const it of picked) {
        if (kind === "reject") await reject(it);
        else await approve(it, kind === "post");
      }
      toast.success(
        kind === "reject" ? `Rejected ${picked.length}` :
        kind === "closet" ? `Saved ${picked.length} to closet` :
        `Posted ${picked.length} to feed`,
      );
      if (kind === "post") {
        // Pre-fill combine composer with the items just approved.
        setCombineJobId(job.id);
        setCombinePicked(new Set(picked.map((p) => p.id)));
        setCombineCaption("");
      }
      setSelectJobId(null);
      setSelectedIds(new Set());
    } finally {
      setBulkBusy(false);
    }
  };

  const retry = async (item: Item) => {
    setBusy(item.id);
    // Re-queue: deduct 1 scan, set status back to queued.
    const { data: gate, error: gErr } = await supabase.functions.invoke("consume-scan", { body: { count: 1 } });
    if (gErr || !gate?.ok) {
      setBusy(null);
      toast.error("Not enough scans to retry");
      return;
    }
    const { error } = await supabase
      .from("bulk_verification_items")
      .update({ status: "queued", error_message: null, completed_at: null })
      .eq("id", item.id);
    if (!error) supabase.functions.invoke("process-bulk-queue", { body: {} })
      .catch((e) => console.warn("process-bulk-queue trigger failed (cron will retry):", e));
    setBusy(null);
    if (error) toast.error("Retry failed"); else toast("Retrying…");
  };

  const approve = async (item: Item, postToFeed: boolean) => {
    if (!user) return;
    setBusy(item.id);
    try {
      const result = (item.verification_result ?? {}) as any;
      const photos = Array.isArray(item.photos) ? (item.photos as string[]) : [];
      const insert = {
        user_id: user.id,
        title: item.item_title || result?.title || "Untitled find",
        brand: result?.brand ?? null,
        era: result?.era ?? null,
        category: result?.category ?? null,
        material: result?.material ?? null,
        size: result?.size ?? null,
        color: result?.color ?? null,
        condition: result?.condition ?? null,
        condition_notes: result?.conditionNotes ?? null,
        verdict: result?.verdict ?? "PASS",
        verdict_label: result?.verdictLabel ?? null,
        confidence: result?.confidence ?? null,
        rarity: result?.rarity ?? null,
        tags: Array.isArray(result?.tags) ? result.tags : [],
        notes: Array.isArray(result?.notes) ? result.notes : [],
        img: photos[0] || "olive",
        photos,
        price_suggested: result?.pricing?.suggested ?? result?.price_suggested ?? null,
        price_low: result?.pricing?.low ?? result?.price_low ?? null,
        price_high: result?.pricing?.high ?? result?.price_high ?? null,
        status: "kept",
        posted: postToFeed,
        verification: result?.verification ?? null,
      };
      const { error: sErr } = await supabase.from("scans").insert(insert as any);
      if (sErr) throw sErr;
      const { error: dErr } = await supabase
        .from("bulk_verification_items")
        .update({ user_decision: "approved" })
        .eq("id", item.id);
      if (dErr) throw dErr;
      toast.success(postToFeed ? "Posted to feed" : "Saved to closet");
    } catch (e) {
      console.error(e);
      toast.error("Approve failed");
    } finally {
      setBusy(null);
    }
  };

  const totalPending = useMemo(
    () => jobs.reduce((n, j) => n + j.items.filter((i) => i.status === "complete" && i.user_decision === "pending").length, 0),
    [jobs],
  );

  /**
   * Hydrate scanStore from a completed bulk item's stored AI result and route
   * to the standard /result page — same Fig Verified report (info, pricing,
   * marketplaces, verification block) the single-item flow shows. We also
   * pre-clear `fig_scan_saved_id` so Result can re-link to a freshly-saved
   * scan row when the user later approves.
   */
  const openReport = (item: Item) => {
    if (item.status !== "complete" || !item.verification_result) {
      toast("Not ready yet");
      return;
    }
    const result = item.verification_result as unknown as AIScan;
    const photos = Array.isArray(item.photos) ? (item.photos as string[]) : [];
    scanStore.setResult(result);
    scanStore.setPhotos(photos);
    // CRITICAL: clear any prior FIG Verify payload from a different scan.
    // Result.tsx reads forensic + pricing from scanStore.getVerifyResult(),
    // which lives in a separate sessionStorage key. Without this, opening
    // bulk item B would render the *previous* item's forensic/pricing.
    scanStore.clearVerifyResult();
    sessionStorage.removeItem("fig_scan_saved_id");
    sessionStorage.setItem("fig_bulk_item_id", item.id);
    nav("/result");
  };

  /** Approved items in a job, eligible for the combined-post composer. */
  const approvedIn = (j: JobWithItems) =>
    j.items.filter((i) => i.status === "complete" && i.user_decision === "approved");

  const openCombine = (j: JobWithItems) => {
    const approved = approvedIn(j);
    setCombineJobId(j.id);
    setCombinePicked(new Set(approved.map((i) => i.id))); // start with all picked
    setCombineCaption("");
  };
  const togglePicked = (id: string) => setCombinePicked((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });

  const submitCombined = async () => {
    if (!user || !combineJobId) return;
    const job = jobs.find((j) => j.id === combineJobId);
    if (!job) return;
    const picked = approvedIn(job).filter((i) => combinePicked.has(i.id));
    if (picked.length < 2) {
      toast.error("Pick at least 2 items to combine");
      return;
    }
    setCombinePosting(true);
    try {
      // Use the first photo from each picked item as the post gallery, capped
      // at 10 to keep feed cards sane. The cover image is the first one.
      const combinedPhotos = picked
        .map((p) => (Array.isArray(p.photos) ? (p.photos as string[])[0] : null))
        .filter((u): u is string => !!u)
        .slice(0, 10);
      const titles = picked.map((p) => p.item_title || "Untitled");
      const composedTitle =
        titles.length === 1 ? titles[0]
        : titles.length === 2 ? `${titles[0]} + ${titles[1]}`
        : `${titles[0]} + ${titles.length - 1} more`;
      const insert = {
        user_id: user.id,
        title: composedTitle,
        caption: combineCaption.trim() || null,
        photos: combinedPhotos,
        img: combinedPhotos[0] || "olive",
        verdict: "PASS",
        post_kind: "manual",
        posted: true,
        status: "kept",
        tags: [],
        notes: [],
        marketplace_links: [],
      };
      const { error } = await supabase.from("scans").insert(insert as any);
      if (error) throw error;
      toast.success(`Posted ${picked.length} items as one`);
      setCombineJobId(null);
      nav("/feed");
    } catch (e) {
      console.error(e);
      toast.error("Could not post combined");
    } finally {
      setCombinePosting(false);
    }
  };

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", padding: "20px 18px 80px" }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
          <h1 style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 26, margin: 0 }}>Bulk review</h1>
          <button onClick={() => nav(-1)} style={{ background: "none", border: "none", cursor: "pointer", fontFamily: "monospace", fontSize: 11 }}>Close</button>
        </div>
        <div style={{ marginTop: 4, fontFamily: "'Fraunces', 'Cormorant Garamond', serif", color: GOLD, fontSize: 13 }}>
          {totalPending > 0 ? `${totalPending} item${totalPending === 1 ? "" : "s"} waiting on you` : "No items awaiting review"}
        </div>
        <div style={{ marginTop: 10, padding: "10px 12px", background: "hsl(var(--paper-3, var(--cream)))", border: "1px dashed hsl(var(--ink))", fontFamily: "'Fraunces', 'Cormorant Garamond', serif", fontSize: 13, color: "hsl(var(--ink))" }}>
          ✦ Tip: approve multiple items and you can combine them into a single feed post.
        </div>

        {loading ? (
          <div style={{ marginTop: 30, fontFamily: "'Fraunces', 'Cormorant Garamond', serif", color: "hsl(var(--ink-2))" }}>Loading…</div>
        ) : jobs.length === 0 ? (
          <div style={{ marginTop: 40, textAlign: "center" }}>
            <p style={{ fontFamily: "'Fraunces', 'Cormorant Garamond', serif", fontSize: 16 }}>No bulk jobs yet.</p>
            <button onClick={() => nav("/bulk")} style={{ marginTop: 12, padding: "10px 16px", background: PLUM, color: PLUM_FG, border: `1px solid ${GOLD}`, cursor: "pointer", fontFamily: "monospace", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}>Start a bulk verify →</button>
          </div>
        ) : (
          <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
            {jobs.map((j) => {
              const open = openId === j.id;
              const pendingCount = j.items.filter((i) => i.status === "complete" && i.user_decision === "pending").length;
              const progressPct = j.total_items ? Math.round((j.completed_items / j.total_items) * 100) : 0;
              const date = new Date(j.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
              return (
                <div key={j.id} style={{ border: "1px solid hsl(var(--ink))", background: "hsl(var(--cream))" }}>
                  <button
                    onClick={() => setOpenId(open ? null : j.id)}
                    style={{ width: "100%", textAlign: "left", padding: "12px 14px", background: "transparent", border: "none", cursor: "pointer" }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 16, fontWeight: 700 }}>{j.total_items} item{j.total_items === 1 ? "" : "s"} · {date}</div>
                        <div className="caps" style={{ fontSize: 10, marginTop: 4, color: "hsl(var(--ink-2))" }}>
                          {j.status === "processing" ? `Verifying ${j.completed_items}/${j.total_items}` : j.status === "partial" ? "Some items failed" : "Complete"}
                          {pendingCount > 0 && ` · ${pendingCount} to review`}
                        </div>
                      </div>
                      <div style={{ fontFamily: "monospace", fontSize: 11 }}>{open ? "▾" : "▸"}</div>
                    </div>
                    {j.status === "processing" && (
                      <div style={{ marginTop: 8, height: 4, background: "hsl(var(--paper-3, var(--cream)))", border: "1px solid hsl(var(--ink))" }}>
                        <div style={{ height: "100%", width: `${progressPct}%`, background: GOLD, transition: "width 0.4s" }} />
                      </div>
                    )}
                  </button>

                  {open && (
                    <div style={{ borderTop: "1px solid hsl(var(--ink))", display: "flex", flexDirection: "column" }}>
                      {j.items.length === 0 && (
                        <div style={{ padding: 14, fontFamily: "'Fraunces', 'Cormorant Garamond', serif", color: "hsl(var(--ink-2))" }}>No items in this job.</div>
                      )}
                      {isJobFullyVerified(j) && pendingCount > 0 && (
                        <div style={{ padding: "10px 12px", borderBottom: "1px solid hsl(var(--ink))", display: "flex", justifyContent: "space-between", alignItems: "center", background: "hsl(var(--paper-3, var(--cream)))" }}>
                          <span className="caps" style={{ fontSize: 10, color: "hsl(var(--ink-2))" }}>
                            {selectJobId === j.id ? `${[...selectedIds].filter((id) => j.items.some((it) => it.id === id)).length} selected` : "Multi-select available"}
                          </span>
                          <button
                            onClick={() => toggleSelectMode(j.id)}
                            style={{ padding: "6px 10px", background: selectJobId === j.id ? PLUM : "transparent", color: selectJobId === j.id ? PLUM_FG : "hsl(var(--ink))", border: "1px solid hsl(var(--ink))", cursor: "pointer", fontFamily: "monospace", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}
                          >
                            {selectJobId === j.id ? "Cancel" : "Select items"}
                          </button>
                        </div>
                      )}
                      {j.items.map((it) => (
                        <ItemRow
                          key={it.id}
                          item={it}
                          busy={busy === it.id}
                          selectMode={selectJobId === j.id}
                          selected={selectedIds.has(it.id)}
                          onToggleSelected={() => toggleSelected(it.id)}
                          onOpen={() => openReport(it)}
                          onApproveCloset={() => void approve(it, false)}
                          onApprovePost={() => void approve(it, true)}
                          onReject={() => void reject(it)}
                          onRetry={() => void retry(it)}
                        />
                      ))}
                      {selectJobId === j.id && (
                        <div style={{ padding: 12, borderTop: "1px solid hsl(var(--ink))", display: "flex", flexDirection: "column", gap: 8, background: "hsl(var(--paper-3, var(--cream)))" }}>
                          <button onClick={() => void bulkApply(j, "post")} disabled={bulkBusy} style={{ padding: 10, background: GOLD, color: GOLD_FG, border: `1px solid ${PLUM}`, cursor: "pointer", fontFamily: "monospace", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}>
                            ✦ Combine post selected
                          </button>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button onClick={() => void bulkApply(j, "closet")} disabled={bulkBusy} style={{ flex: 1, padding: 10, background: PLUM, color: PLUM_FG, border: `1px solid ${GOLD}`, cursor: "pointer", fontFamily: "monospace", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}>Keep in closet</button>
                            <button onClick={() => void bulkApply(j, "reject")} disabled={bulkBusy} style={{ flex: 1, padding: 10, background: "transparent", color: "hsl(var(--ink))", border: "1px solid hsl(var(--ink))", cursor: "pointer", fontFamily: "monospace", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}>Reject all</button>
                          </div>
                        </div>
                      )}
                      {approvedIn(j).length >= 2 && (
                        <button
                          onClick={() => openCombine(j)}
                          style={{ margin: 12, padding: "10px 12px", background: GOLD, color: GOLD_FG, border: "none", cursor: "pointer", fontFamily: "monospace", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}
                        >
                          ✦ Combine {approvedIn(j).length} approved into one post
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {combineJobId && (() => {
        const job = jobs.find((j) => j.id === combineJobId);
        if (!job) return null;
        const approved = approvedIn(job);
        const pickedCount = approved.filter((i) => combinePicked.has(i.id)).length;
        return (
          <div role="dialog" aria-modal="true" onClick={() => !combinePosting && setCombineJobId(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 210, display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: "hsl(var(--cream))", color: "hsl(var(--ink))", padding: 20, paddingBottom: "calc(20px + 80px + env(safe-area-inset-bottom, 0px))", maxWidth: 460, width: "100%", maxHeight: "90vh", overflowY: "auto", border: `1px solid ${GOLD}`, borderBottom: "none" }}>
              <h2 style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 22, margin: 0 }}>Combined post</h2>
              <p style={{ marginTop: 4, fontFamily: "'Fraunces', 'Cormorant Garamond', serif", fontSize: 13, color: "hsl(var(--ink-2))" }}>
                Pick the approved pieces to combine — first one becomes the cover.
              </p>
              <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))", gap: 8 }}>
                {approved.map((it) => {
                  const photos = Array.isArray(it.photos) ? (it.photos as string[]) : [];
                  const thumb = it.thumbnail_url || photos[0];
                  const picked = combinePicked.has(it.id);
                  return (
                    <button
                      key={it.id}
                      onClick={() => togglePicked(it.id)}
                      title={it.item_title ?? "Untitled"}
                      style={{ position: "relative", padding: 0, border: picked ? `2px solid ${GOLD}` : "1px solid hsl(var(--ink))", background: "hsl(var(--paper-3, var(--cream)))", cursor: "pointer", aspectRatio: "1", overflow: "hidden" }}
                    >
                      {thumb ? <img src={thumb} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", opacity: picked ? 1 : 0.55 }} /> : null}
                      {picked && (
                        <span style={{ position: "absolute", top: 2, right: 4, color: PLUM_FG, background: PLUM, fontSize: 10, padding: "1px 4px", fontFamily: "monospace", fontWeight: 700 }}>✓</span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div style={{ marginTop: 12, fontFamily: "monospace", fontSize: 11, color: "hsl(var(--ink-2))" }}>
                {pickedCount} selected · max 10 photos in feed
              </div>
              <textarea
                value={combineCaption}
                onChange={(e) => setCombineCaption(e.target.value.slice(0, 600))}
                placeholder="Caption for the combined post…"
                style={{ marginTop: 10, width: "100%", boxSizing: "border-box", minHeight: 70, padding: 8, fontFamily: "'Fraunces', 'Cormorant Garamond', serif", fontSize: 14, border: "1px solid hsl(var(--ink))", background: "hsl(var(--paper))" }}
              />
              <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
                <button onClick={() => setCombineJobId(null)} disabled={combinePosting} style={{ flex: 1, padding: 10, background: "transparent", color: "hsl(var(--ink))", border: "1px solid hsl(var(--ink))", cursor: "pointer", fontFamily: "monospace", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase" }}>Cancel</button>
                <button onClick={() => void submitCombined()} disabled={combinePosting || pickedCount < 2} style={{ flex: 2, padding: 10, background: PLUM, color: PLUM_FG, border: `1px solid ${GOLD}`, cursor: "pointer", fontFamily: "monospace", fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", opacity: pickedCount < 2 ? 0.5 : 1 }}>
                  {combinePosting ? "Posting…" : `Post combined (${pickedCount})`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </MobileFrame>
  );
}

function ItemRow({ item, busy, selectMode, selected, onToggleSelected, onOpen, onApproveCloset, onApprovePost, onReject, onRetry }: { item: Item; busy: boolean; selectMode?: boolean; selected?: boolean; onToggleSelected?: () => void; onOpen: () => void; onApproveCloset: () => void; onApprovePost: () => void; onReject: () => void; onRetry: () => void; }) {
  const photos = Array.isArray(item.photos) ? (item.photos as string[]) : [];
  const thumb = item.thumbnail_url || photos[0];
  const decided = item.user_decision !== "pending";
  const openable = item.status === "complete" && !!item.verification_result;
  const selectable = selectMode && item.status === "complete" && !decided;

  return (
    <div style={{ display: "flex", gap: 12, padding: 12, borderBottom: "1px solid hsl(var(--ink))", alignItems: "flex-start", opacity: decided ? 0.55 : 1, background: selected ? "hsl(var(--marigold) / 0.15)" : "transparent" }}>
      {selectMode && (
        <button
          onClick={selectable ? onToggleSelected : undefined}
          disabled={!selectable}
          aria-label={selected ? "Unselect" : "Select"}
          style={{ width: 22, height: 22, flexShrink: 0, marginTop: 18, border: "1px solid hsl(var(--ink))", background: selected ? PLUM : "hsl(var(--paper))", color: selected ? PLUM_FG : "hsl(var(--ink))", cursor: selectable ? "pointer" : "not-allowed", fontFamily: "monospace", fontSize: 12, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", opacity: selectable ? 1 : 0.4 }}
        >
          {selected ? "✓" : ""}
        </button>
      )}
      <button
        onClick={openable ? onOpen : undefined}
        disabled={!openable}
        aria-label={openable ? "Open verification report" : undefined}
        style={{ width: 56, height: 56, flexShrink: 0, border: "1px solid hsl(var(--ink))", background: "hsl(var(--paper-3, var(--cream)))", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", padding: 0, cursor: openable ? "pointer" : "default" }}
      >
        {thumb ? <img src={thumb} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <span style={{ fontSize: 18 }}>·</span>}
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <button
          onClick={openable ? onOpen : undefined}
          disabled={!openable}
          style={{ background: "none", border: "none", padding: 0, textAlign: "left", cursor: openable ? "pointer" : "default", color: "inherit", width: "100%" }}
        >
          <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 14, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: openable ? "underline" : "none", textDecorationColor: "hsl(var(--marigold))", textUnderlineOffset: 3 }}>
            {item.item_title || (item.status === "queued" ? "Queued…" : item.status === "processing" ? "Verifying…" : item.status === "failed" ? "Verification failed" : "Untitled")}
          </div>
          <div className="caps" style={{ fontSize: 9, marginTop: 2, color: "hsl(var(--ink-2))" }}>
            {item.status === "complete" && item.is_verified && "PASS"}
            {item.status === "complete" && item.is_verified === false && "FLAG"}
            {item.price_suggestion && ` · ${item.price_suggestion}`}
            {decided && ` · ${item.user_decision}`}
            {openable && " · tap for full report →"}
          </div>
        </button>
        {item.status === "failed" && item.error_message && (
          <div style={{ marginTop: 4, fontFamily: "'Fraunces', 'Cormorant Garamond', serif", fontSize: 12, color: "hsl(var(--ink-2))" }}>
            {item.error_message.slice(0, 120)}
          </div>
        )}

        {item.status === "complete" && !decided && (
          <div style={{ marginTop: 8, display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button onClick={onApprovePost} disabled={busy} style={{ padding: "6px 10px", background: GOLD, color: GOLD_FG, border: `1px solid ${PLUM}`, borderRadius: 999, cursor: "pointer", fontFamily: "monospace", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}>Post to feed</button>
            <button onClick={onApproveCloset} disabled={busy} style={{ padding: "6px 10px", background: PLUM, color: PLUM_FG, border: `1px solid ${PLUM}`, borderRadius: 999, cursor: "pointer", fontFamily: "monospace", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}>Keep in closet</button>
            <button onClick={onReject} disabled={busy} style={{ padding: "6px 10px", background: "transparent", color: "hsl(var(--ink))", border: "1px solid hsl(var(--ink))", borderRadius: 999, cursor: "pointer", fontFamily: "monospace", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}>Reject</button>
          </div>
        )}
        {item.status === "failed" && (
          <button onClick={onRetry} disabled={busy} style={{ marginTop: 6, padding: "6px 10px", background: "transparent", color: "hsl(var(--ink))", border: "1px solid hsl(var(--ink))", cursor: "pointer", fontFamily: "monospace", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase" }}>Retry (1 scan)</button>
        )}
        {(item.status === "queued" || item.status === "processing") && (
          <div style={{ marginTop: 6, fontFamily: "monospace", fontSize: 10, color: GOLD }}>
            {item.status === "queued" ? "○ queued" : "◐ verifying…"}
          </div>
        )}
      </div>
    </div>
  );
}
